import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { ArrowRight, Plus, Trash2, DollarSign, ArrowLeftRight, AlertTriangle, Building2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CrossCharges() {
  const { token, user } = useAuth();
  
  const [crossCharges, setCrossCharges] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [voidDialogOpen, setVoidDialogOpen] = useState(false);
  const [voidingCharge, setVoidingCharge] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    sourceJobId: '',
    targetJobId: '',
    entryType: 'LABOR',
    amount: '',
    reason: ''
  });

  const isOffice = user?.role === 'OFFICE' || user?.role === 'OWNER';

  useEffect(() => {
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      const [crossChargesRes, jobsRes] = await Promise.all([
        axios.get(`${API}/cross-charges`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/jobs`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setCrossCharges(crossChargesRes.data);
      setJobs(jobsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load cross-charges');
    } finally {
      setLoading(false);
    }
  };

  const getJobDisplay = (jobId) => {
    const job = jobs.find(j => j.id === jobId);
    return job ? `${job.jobNumber} - ${job.name}` : 'Unknown Job';
  };

  const handleOpenDialog = () => {
    setFormData({
      sourceJobId: '',
      targetJobId: '',
      entryType: 'LABOR',
      amount: '',
      reason: ''
    });
    setDialogOpen(true);
  };

  const activeJobs = useMemo(() => 
    jobs.filter(j => j.status === 'ACTIVE' || j.status === 'COMPLETED'),
    [jobs]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.sourceJobId || !formData.targetJobId || !formData.amount || !formData.reason) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    if (formData.sourceJobId === formData.targetJobId) {
      toast.error('Source and target jobs must be different');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/cross-charges`, {
        ...formData,
        amount: parseFloat(formData.amount)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Cross-charge created successfully');
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Failed to create cross-charge:', error);
      toast.error(error.response?.data?.detail || 'Failed to create cross-charge');
    } finally {
      setSubmitting(false);
    }
  };

  const handleVoid = async () => {
    if (!voidingCharge) return;
    
    try {
      await axios.delete(`${API}/cross-charges/${voidingCharge.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Cross-charge voided successfully');
      setVoidDialogOpen(false);
      setVoidingCharge(null);
      fetchData();
    } catch (error) {
      console.error('Failed to void cross-charge:', error);
      toast.error('Failed to void cross-charge');
    }
  };

  // Group cross-charges by source job
  const groupedBySource = useMemo(() => {
    const groups = {};
    crossCharges.forEach(cc => {
      if (!groups[cc.sourceJobId]) {
        groups[cc.sourceJobId] = [];
      }
      groups[cc.sourceJobId].push(cc);
    });
    return groups;
  }, [crossCharges]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1A3668]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Cross-Charges</h1>
          <p className="text-slate-600 mt-1">Transfer costs between jobs when budgets are exceeded</p>
        </div>
        {isOffice && (
          <Button 
            onClick={handleOpenDialog}
            className="bg-[#1A3668] hover:bg-[#1A3668]/90 rounded-sm w-full sm:w-auto"
            data-testid="create-cross-charge-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Cross-Charge
          </Button>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-slate-200 shadow-sm rounded-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-sm">
                <ArrowLeftRight className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Cross-Charges</p>
                <p className="text-xl font-bold text-slate-900">{crossCharges.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm rounded-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-sm">
                <DollarSign className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Amount Transferred</p>
                <p className="text-xl font-bold text-slate-900">
                  {formatCurrency(crossCharges.reduce((sum, cc) => sum + cc.amount, 0))}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm rounded-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-sm">
                <Building2 className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Jobs Affected</p>
                <p className="text-xl font-bold text-slate-900">
                  {new Set([...crossCharges.map(cc => cc.sourceJobId), ...crossCharges.map(cc => cc.targetJobId)]).size}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Cross-Charges List */}
      {crossCharges.length === 0 ? (
        <Card className="border-slate-200 shadow-sm rounded-sm">
          <CardContent className="p-12 text-center">
            <ArrowLeftRight className="w-12 h-12 mx-auto text-slate-400 mb-4" />
            <p className="text-slate-600 mb-2">No cross-charges yet</p>
            <p className="text-sm text-slate-500">
              Create a cross-charge when a job exceeds its budget and you need to allocate costs to another job's PO.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {crossCharges.map((cc) => (
            <Card key={cc.id} className="border-slate-200 shadow-sm rounded-sm" data-testid={`cross-charge-${cc.id}`}>
              <CardContent className="p-4 md:p-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  {/* Source -> Target */}
                  <div className="flex-1">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 mb-3">
                      <div className="flex-1">
                        <p className="text-xs text-slate-500 uppercase tracking-wide">From (Over Budget)</p>
                        <p className="font-semibold text-slate-900">{getJobDisplay(cc.sourceJobId)}</p>
                      </div>
                      <ArrowRight className="w-5 h-5 text-slate-400 hidden sm:block" />
                      <div className="flex-1">
                        <p className="text-xs text-slate-500 uppercase tracking-wide">To (Covering Cost)</p>
                        <p className="font-semibold text-slate-900">{getJobDisplay(cc.targetJobId)}</p>
                      </div>
                    </div>
                    
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <Badge variant="outline" className="rounded-sm bg-green-50 border-green-200 text-green-700">
                        {formatCurrency(cc.amount)}
                      </Badge>
                      <Badge variant="outline" className="rounded-sm">
                        {cc.entryType}
                      </Badge>
                    </div>
                    
                    <p className="text-sm text-slate-600">
                      <span className="font-medium">Reason:</span> {cc.reason}
                    </p>
                    <p className="text-xs text-slate-400 mt-1">
                      Created: {new Date(cc.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                  
                  {/* Actions */}
                  {isOffice && (
                    <Button
                      onClick={() => {
                        setVoidingCharge(cc);
                        setVoidDialogOpen(true);
                      }}
                      variant="outline"
                      size="sm"
                      className="border-red-200 text-red-600 hover:bg-red-50 rounded-sm"
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Void
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Cross-Charge Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#1A3668]">Create Cross-Charge</DialogTitle>
            <DialogDescription>
              Transfer costs from one job to another when a job exceeds its budget.
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-sm p-3">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-amber-800">
                  <p className="font-medium">How Cross-Charges Work:</p>
                  <ul className="list-disc ml-4 mt-1 space-y-1">
                    <li>The cost stays visible on the <strong>source job</strong> (shows it went over)</li>
                    <li>The cost is also added to the <strong>target job's PO burn</strong></li>
                    <li>Both jobs maintain full audit trail</li>
                  </ul>
                </div>
              </div>
            </div>
            
            <div>
              <Label htmlFor="sourceJob" className="text-sm font-bold uppercase tracking-wider text-slate-500">
                Source Job (Over Budget)
              </Label>
              <Select value={formData.sourceJobId} onValueChange={(v) => setFormData({ ...formData, sourceJobId: v })}>
                <SelectTrigger className="mt-2 rounded-sm border-2" data-testid="source-job-select">
                  <SelectValue placeholder="Select the job that went over budget" />
                </SelectTrigger>
                <SelectContent>
                  {activeJobs.map((job) => (
                    <SelectItem key={job.id} value={job.id}>
                      {job.jobNumber} - {job.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="targetJob" className="text-sm font-bold uppercase tracking-wider text-slate-500">
                Target Job (Covering Cost)
              </Label>
              <Select value={formData.targetJobId} onValueChange={(v) => setFormData({ ...formData, targetJobId: v })}>
                <SelectTrigger className="mt-2 rounded-sm border-2" data-testid="target-job-select">
                  <SelectValue placeholder="Select the job whose PO will cover this cost" />
                </SelectTrigger>
                <SelectContent>
                  {activeJobs.filter(j => j.id !== formData.sourceJobId).map((job) => (
                    <SelectItem key={job.id} value={job.id}>
                      {job.jobNumber} - {job.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="entryType" className="text-sm font-bold uppercase tracking-wider text-slate-500">
                  Cost Type
                </Label>
                <Select value={formData.entryType} onValueChange={(v) => setFormData({ ...formData, entryType: v })}>
                  <SelectTrigger className="mt-2 rounded-sm border-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="LABOR">Labor</SelectItem>
                    <SelectItem value="EQUIPMENT">Equipment</SelectItem>
                    <SelectItem value="MATERIAL">Material</SelectItem>
                    <SelectItem value="OTHER">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="amount" className="text-sm font-bold uppercase tracking-wider text-slate-500">
                  Amount
                </Label>
                <div className="relative mt-2">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                  <Input
                    id="amount"
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    placeholder="0.00"
                    className="rounded-sm border-2 pl-7"
                    data-testid="amount-input"
                  />
                </div>
              </div>
            </div>
            
            <div>
              <Label htmlFor="reason" className="text-sm font-bold uppercase tracking-wider text-slate-500">
                Reason for Cross-Charge
              </Label>
              <Textarea
                id="reason"
                value={formData.reason}
                onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                placeholder="Explain why this cost is being transferred (e.g., 'Job A PO exhausted - remaining labor allocated to Job B per client approval')"
                className="mt-2 rounded-sm border-2"
                rows={3}
                data-testid="reason-input"
              />
            </div>
            
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
                className="rounded-sm border-2"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="bg-[#1A3668] hover:bg-[#1A3668]/90 rounded-sm"
              >
                {submitting ? 'Creating...' : 'Create Cross-Charge'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Void Confirmation Dialog */}
      <AlertDialog open={voidDialogOpen} onOpenChange={setVoidDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Void Cross-Charge?</AlertDialogTitle>
            <AlertDialogDescription>
              This will void the cross-charge of {voidingCharge && formatCurrency(voidingCharge.amount)}. 
              The cost will no longer be counted against the target job's PO.
              This action is recorded in the audit log.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleVoid}
              className="bg-red-600 hover:bg-red-700 rounded-sm"
            >
              Void Cross-Charge
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
